import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { FiBox, FiTool, FiAlertCircle, FiCheckCircle, FiClock, FiActivity, FiPlus, FiDollarSign, FiChevronDown, FiTrendingUp, FiFileText, FiArrowRight } from 'react-icons/fi';
import HelpTip from '../components/HelpTip';
import WorkOrderCreateModal from '../components/WorkOrderCreateModal';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { WO_TYPE_COLORS } from '../utils/constants';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.4, type: 'spring' } })
};

function StatCard({ icon: Icon, label, value, color, index, subtext, tip }) {
  return (
    <motion.div custom={index} variants={cardVariants} initial="hidden" animate="visible"
      whileHover={{ scale: 1.03, y: -4 }}
      style={{
        background: 'var(--bg-card)', borderRadius: 16, padding: '24px',
        border: '1px solid var(--border)', cursor: 'default', position: 'relative', overflow: 'hidden',
        boxShadow: 'var(--shadow)'
      }}
    >
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `${color}10` }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={20} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {subtext && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontWeight: 500 }}>{subtext}</div>}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}
        {tip && <HelpTip text={tip} />}
      </div>
    </motion.div>
  );
}

const statusColors = {
  'New': { bg: 'rgba(217,119,6,0.1)', color: '#d97706' },
  'In Progress': { bg: 'rgba(37,99,235,0.1)', color: '#2563eb' },
  'Complete': { bg: 'rgba(22,163,74,0.1)', color: '#16a34a' },
  'TECO': { bg: 'rgba(6,95,70,0.1)', color: '#065f46' },
};

const CHART_COLORS = ['#2563eb', '#0ea5e9', '#db2777', '#d97706', '#16a34a', '#0891b2', '#ea580c', '#dc2626', '#059669', '#4f46e5', '#0d9488', '#0284c7'];


export default function Dashboard() {
  const { authFetch, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [costData, setCostData] = useState(null);
  const [costMonth, setCostMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const [topAssets, setTopAssets] = useState([]);
  const [topAssetDays, setTopAssetDays] = useState(30);
  const [pm02Health, setPm02Health] = useState(null);
  const [preReadStatus, setPreReadStatus] = useState(null); // { preRead, operations, frozen }
  const [availabilityHistory, setAvailabilityHistory] = useState([]);
  const [remainingHistory, setRemainingHistory] = useState({ months: [], types: [] });
  const isSupervisor = user?.role === 'supervisor' || user?.role === 'admin' || (user?.permissions && (user.permissions.includes('supervisor_panel') || user.permissions.includes('admin_panel')));
  useEffect(() => {
    const n = new Date();
    const currentMonth = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
    authFetch(`/api/stats/costs?month=${currentMonth}`).then(r => r.ok ? r.json() : null).then(setCostData).catch(() => {});
    authFetch('/api/stats/top-assets?days=30&limit=5').then(r => r.ok ? r.json() : []).then(d => setTopAssets(Array.isArray(d) ? d : [])).catch(() => {});
    authFetch('/api/issues/pm02-health').then(r => r.ok ? r.json() : null).then(setPm02Health).catch(() => {});
    authFetch('/api/stats/asset-availability-history?months=12').then(r => r.ok ? r.json() : []).then(d => setAvailabilityHistory(Array.isArray(d) ? d : [])).catch(() => {});
    authFetch('/api/stats/remaining-wo-history?months=12').then(r => r.ok ? r.json() : null).then(d => { if (d && Array.isArray(d.months)) setRemainingHistory(d); }).catch(() => {});
    if (isSupervisor) {
      authFetch('/api/issues/pre-read').then(r => r.ok ? r.json() : null).then(setPreReadStatus).catch(() => {});
    }
  }, []);
  const loadTopAssets = (days) => { setTopAssetDays(days); authFetch(`/api/stats/top-assets?days=${days}&limit=5`).then(r => r.ok ? r.json() : []).then(d => setTopAssets(Array.isArray(d) ? d : [])).catch(() => {}); };
  const loadCosts = (m) => { setCostMonth(m); authFetch(`/api/stats/costs${m ? `?month=${m}` : ''}`).then(r => r.ok ? r.json() : null).then(setCostData).catch(() => {}); };
  useEffect(() => {
    const n = new Date(); const td = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
    authFetch(`/api/stats?today=${td}`).then(r => r.json()).then(data => { setStats(data); setLoading(false); });
  }, []);

  // Live polling every 20s — refreshes the stats grid AND every secondary
  // panel (costs, top assets, PM02 health, asset availability trend) so
  // the dashboard is genuinely live with no manual refresh.
  useEffect(() => {
    const interval = setInterval(() => {
      const n = new Date(); const td = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
      const m = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
      authFetch(`/api/stats?today=${td}`).then(r => r.json()).then(data => {
        setStats(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
      }).catch(() => {});
      authFetch(`/api/stats/costs?month=${m}`).then(r => r.ok ? r.json() : null).then(d => setCostData(prev => JSON.stringify(prev) !== JSON.stringify(d) ? d : prev)).catch(() => {});
      authFetch(`/api/stats/top-assets?days=${topAssetDays}&limit=5`).then(r => r.ok ? r.json() : []).then(d => setTopAssets(prev => JSON.stringify(prev) !== JSON.stringify(d) ? (Array.isArray(d) ? d : []) : prev)).catch(() => {});
      authFetch('/api/issues/pm02-health').then(r => r.ok ? r.json() : null).then(d => setPm02Health(prev => JSON.stringify(prev) !== JSON.stringify(d) ? d : prev)).catch(() => {});
      authFetch('/api/stats/asset-availability-history?months=12').then(r => r.ok ? r.json() : []).then(d => setAvailabilityHistory(prev => JSON.stringify(prev) !== JSON.stringify(d) ? (Array.isArray(d) ? d : []) : prev)).catch(() => {});
      authFetch('/api/stats/remaining-wo-history?months=12').then(r => r.ok ? r.json() : null).then(d => { if (d && Array.isArray(d.months)) setRemainingHistory(prev => JSON.stringify(prev) !== JSON.stringify(d) ? d : prev); }).catch(() => {});
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div className="skeleton" style={{ width: 260, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 220, height: 16 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 28 }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)' }}>
            <div className="skeleton" style={{ width: 42, height: 42, borderRadius: 12, marginBottom: 12 }} />
            <div className="skeleton" style={{ width: 60, height: 32, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: 100, height: 14 }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)' }}>
            <div className="skeleton" style={{ width: 160, height: 18, marginBottom: 20 }} />
            <div className="skeleton" style={{ width: '100%', height: 260 }} />
          </div>
        ))}
      </div>
    </div>
  );

  const tooltipStyle = { background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, color: isDark ? '#f1f5f9' : '#1e293b', boxShadow: 'var(--shadow)' };
  const tooltipLabelStyle = { color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 600 };
  const tooltipItemStyle = { color: isDark ? '#94a3b8' : '#64748b' };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)', margin: 0 }}>
          Welcome back, <span style={{ color: 'var(--accent)' }}>{user?.name || ''}</span>
        </h1>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
            cursor: 'pointer', fontSize: 14, fontWeight: 600, boxShadow: '0 2px 8px rgba(37,99,235,0.3)'
          }}
        ><FiPlus size={16} /> New Notification</motion.button>
      </motion.div>

      {/* Weekly Pre-Read badge — supervisors only, shown when next-week pre-read has ops and hasn't been submitted */}
      {isSupervisor && preReadStatus && !preReadStatus.frozen && (preReadStatus.operations?.length || 0) > 0 && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate('/supervisor?tab=pre-read')}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 12, background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(139,92,246,0.08))', border: '1px solid rgba(37,99,235,0.25)', cursor: 'pointer', marginBottom: 16, boxShadow: '0 2px 8px rgba(37,99,235,0.08)' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiFileText size={20} color="#2563eb" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#2563eb' }}>Pre-Read Now Available</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {preReadStatus.operations.length} operation{preReadStatus.operations.length !== 1 ? 's' : ''} waiting for review for next week — click to review and submit
            </div>
          </div>
          <FiArrowRight size={18} color="#2563eb" />
        </motion.div>
      )}

      {/* My Working Week */}
      {stats.myWeeklyHours !== undefined && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          style={{
            background: 'var(--bg-card)', borderRadius: 16, padding: '20px 24px',
            border: '1px solid var(--border)', boxShadow: 'var(--shadow)', marginBottom: 20
          }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiClock size={18} color="#8b5cf6" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>My Working Week</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(stats.weekStartDate)} — {formatDate(stats.weekEndDate)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: '#8b5cf6' }}>{stats.myOpsConfirmed || 0}</span>
              <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 500 }}>/ {stats.myOpsTotal || 0} ops total</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#8b5cf6', marginLeft: 4 }}>
                {stats.myOpsTotal > 0 ? Math.round(((stats.myOpsConfirmed || 0) / stats.myOpsTotal) * 100) : 0}%
              </span>
            </div>
          </div>
          <div style={{ position: 'relative', height: 10, borderRadius: 6, background: isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.08)', overflow: 'hidden' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${stats.myOpsTotal > 0 ? Math.min(((stats.myOpsConfirmed || 0) / stats.myOpsTotal) * 100, 100) : 0}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              style={{ position: 'absolute', top: 0, left: 0, height: '100%', borderRadius: 6, background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {stats.myOpsTotal > 0 ? (stats.myOpsConfirmed >= stats.myOpsTotal ? 'All confirmed' : `${stats.myOpsTotal || 0} total ops assigned this week`) : 'No operations this week'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {stats.myOpsTotal > 0 ? Math.round(((stats.myOpsConfirmed || 0) / stats.myOpsTotal) * 100) : 0}% &middot; {(() => { const s = Math.round(parseFloat(stats.myWeeklyHours) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; })()} logged
            </span>
          </div>
        </motion.div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard icon={FiBox} label="Total Assets" value={stats.totalAssets} color="#2563eb" index={0}
          tip="Every asset registered in this site, including Active, Standby, Faulty, and Redundant. Add or retire assets in Search Assets." />
        <StatCard icon={FiTool} label="My Week"
          value={`${stats.myOpsConfirmed ?? 0}/${stats.myOpsTotal ?? 0}`}
          subtext="confirmed this week"
          color="#0ea5e9" index={1}
          tip="Operations assigned to you with a planned date in the current Mon–Sun week. The first number is how many you've already final-time-confirmed." />
        <StatCard icon={FiAlertCircle} label="New" value={stats.todoIssues} color="#d97706" index={2}
          tip="Work orders with status 'New' — created but not yet started." />
        <StatCard icon={FiClock} label="In Progress" value={stats.inProgressIssues} color="#0891b2" index={3}
          tip="Work orders that have at least one operation underway but haven't been TECO'd yet." />
        <StatCard icon={FiCheckCircle} label="Completed" value={stats.doneIssues} color="#16a34a" index={4}
          tip="Work orders fully marked Complete. They become TECO'd once a supervisor signs them off." />
        <StatCard icon={FiActivity} label="Open Issues" value={stats.openIssues} color="#dc2626" index={5}
          tip="Total non-TECO'd work orders — anything still on the team's plate (sum of New + In Progress + Complete-pending-TECO)." />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 28 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Asset Status</h3>
            {stats.assetAvailabilityPct != null && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                {stats.assetAvailabilityPct}% Available
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.statusCounts}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>{stats.statusCounts.map((entry, i) => (<Cell key={i} fill={entry.color} />))}</Bar>
            </BarChart>
          </ResponsiveContainer>
          {availabilityHistory.length > 1 && (() => {
            const data = availabilityHistory.map(r => ({
              month: new Date(r.month_start).toLocaleString('en-AU', { month: 'short', year: '2-digit' }),
              pct: r.availability_pct,
            }));
            return (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>Active Assets — Last 12 Months</div>
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={data} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" width={42} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(v) => `${v}%`} />
                    <Line type="monotone" dataKey="pct" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Remaining Work Orders by Type</h3>
            <HelpTip text="Work orders that haven't been TECO'd yet. Counts go down as supervisors sign work off." />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.issuesByType} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis dataKey="type" type="category" tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} width={100} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} formatter={(v) => [`${v} work order${v !== 1 ? 's' : ''}`, 'Remaining']} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>{stats.issuesByType.map((entry, i) => (<Cell key={i} fill={WO_TYPE_COLORS[entry.type] || CHART_COLORS[i % CHART_COLORS.length]} />))}</Bar>
            </BarChart>
          </ResponsiveContainer>
          {remainingHistory.months.length > 1 && remainingHistory.types.length > 0 && (() => {
            // Each month is a column; horizontal scroll lets the user
            // page back through end-of-month snapshots even if the screen
            // is narrow.
            const fmtMonth = (s) => {
              try { const d = new Date(s + 'T12:00:00'); return d.toLocaleString('en-AU', { month: 'short', year: '2-digit' }); }
              catch { return s; }
            };
            const data = remainingHistory.months.map(m => ({
              ...m,
              label: fmtMonth(m.month_start),
            }));
            const minWidth = Math.max(data.length * 70, 320);
            return (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>End-of-Month History</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{data.length} month{data.length !== 1 ? 's' : ''} · scroll →</div>
                </div>
                <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
                  <div style={{ minWidth, height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data} margin={{ top: 6, right: 12, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} width={42} />
                        <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
                        {remainingHistory.types.map((t, i) => (
                          <Line key={t} type="monotone" dataKey={t} stroke={WO_TYPE_COLORS[t] || CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            );
          })()}
        </motion.div>
      </div>

      {/* Cost Overview */}
      {costData && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiDollarSign size={18} color="#059669" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>Cost Overview <HelpTip text="Shows costs from time confirmations (labour × hourly rate) and materials added to work orders. Set hourly rates on user profiles and add costs to materials for accurate tracking." /></div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {costData.period?.start && `${formatDate(costData.period.start)} — ${formatDate(costData.period.end)}`}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="month" value={costMonth}
                onChange={e => loadCosts(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}
              />
              {costMonth && <button onClick={() => loadCosts('')} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11 }}>Current</button>}
            </div>
          </div>

          {/* Total + breakdown cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 16 }}>
            <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.12)', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#2563eb' }}>${costData.total?.toLocaleString()}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</div>
              {costData.prev != null && costData.total > 0 && (() => {
                const diff = costData.total - costData.prev;
                const pct = costData.prev > 0 ? Math.round((diff / costData.prev) * 100) : (costData.total > 0 ? 100 : 0);
                const up = diff > 0;
                const same = diff === 0;
                return (
                  <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4, color: same ? 'var(--text-muted)' : up ? '#ef4444' : '#16a34a' }}>
                    {same ? '— Same as last month' : `${up ? '▲' : '▼'} ${Math.abs(pct)}% vs last month`}
                  </div>
                );
              })()}
            </div>
            <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.12)', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#059669' }}>${costData.labour?.toLocaleString()}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Labour</div>
            </div>
            <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#f59e0b' }}>${costData.parts?.toLocaleString()}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Parts</div>
            </div>
            <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.12)', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0ea5e9' }}>${costData.services?.toLocaleString()}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Services</div>
            </div>
            <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#8b5cf6' }}>${costData.ext_labour?.toLocaleString()}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Ext. Labour</div>
            </div>
          </div>

          {/* Pie chart + trend */}
          <div style={{ display: 'grid', gridTemplateColumns: costData.breakdown?.length > 0 && costData.trend?.length > 0 ? '1fr 1fr' : '1fr', gap: 16 }}>
            {costData.breakdown?.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Cost Split</div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={costData.breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {costData.breakdown.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={v => `$${v.toLocaleString()}`} contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4, justifyContent: 'center' }}>
                  {costData.breakdown.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-secondary)' }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                      {d.name}: ${d.value.toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {costData.trend?.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Monthly Labour Trend</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={costData.trend}>
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                    <Tooltip formatter={v => `$${v.toLocaleString()}`} contentStyle={tooltipStyle} />
                    <Bar dataKey="labour" radius={[4, 4, 0, 0]} fill="#059669" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {costData.total === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 13 }}>No cost data for this period</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Set hourly rates on user profiles and add material costs to work orders</div>
            </div>
          )}
        </motion.div>
      )}

      {/* PM02 Template Health */}
      {pm02Health && pm02Health.data && pm02Health.data.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiActivity size={18} color="#8b5cf6" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  PM02 Template Health <HelpTip text="Health of your preventive maintenance templates. Critical = overdue or too many open WOs. Warning = slipping behind schedule. Healthy = on track." />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{pm02Health.summary.total} active schedule{pm02Health.summary.total !== 1 ? 's' : ''}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'rgba(22,163,74,0.1)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.25)' }}>{pm02Health.summary.healthy} Healthy</span>
              {pm02Health.summary.warning > 0 && <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>{pm02Health.summary.warning} Warning</span>}
              {pm02Health.summary.critical > 0 && <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}>{pm02Health.summary.critical} Critical</span>}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {pm02Health.data
              .filter(t => t.health !== 'healthy')
              .slice(0, 8)
              .map(t => {
                const healthColor = t.health === 'critical' ? '#ef4444' : t.health === 'warning' ? '#f59e0b' : '#16a34a';
                return (
                  <div key={t.schedule_id} onClick={() => t.asset_id && navigate(`/assets/${t.asset_id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: `1px solid ${healthColor}30`, borderLeft: `4px solid ${healthColor}`, cursor: t.asset_id ? 'pointer' : 'default' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t.template_name}</span>
                        {t.asset_tag && <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)' }}>{t.asset_tag}</span>}
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: `${healthColor}20`, color: healthColor, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t.health}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {t.asset_name && <>{t.asset_name} · </>}
                        {t.frequency} · Next: {t.next_due ? formatDate(t.next_due) : '—'}
                        {t.days_overdue > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}> · {t.days_overdue}d overdue</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {t.open_count > 0 && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#0ea5e9' }}>{t.open_count}</div>
                          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>OPEN</div>
                        </div>
                      )}
                      {t.overdue_count > 0 && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#ef4444' }}>{t.overdue_count}</div>
                          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>OVERDUE</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            {pm02Health.summary.warning === 0 && pm02Health.summary.critical === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>
                <FiCheckCircle size={24} color="#16a34a" style={{ marginBottom: 6 }} />
                <div>All {pm02Health.summary.total} PM02 templates are healthy</div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Top Utilised Assets */}
      {topAssets.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(37,99,235,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiTrendingUp size={18} color="#2563eb" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Top Utilised Assets <HelpTip text="Assets with the most hours worked in the selected period. Click any asset to see its full utilization details and cost breakdown." />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Last {topAssetDays} days — top 5 by hours worked</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 3, background: 'var(--bg-input)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
              {[{ val: 30, label: '30d' }, { val: 90, label: '90d' }, { val: 365, label: '1y' }].map(r => (
                <button key={r.val} onClick={() => loadTopAssets(r.val)}
                  style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: topAssetDays === r.val ? '#2563eb' : 'transparent', color: topAssetDays === r.val ? '#fff' : 'var(--text-secondary)' }}>{r.label}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(() => {
              const maxHours = Math.max(...topAssets.map(a => a.total_hours), 1);
              return topAssets.map((a, i) => {
                const pct = Math.round((a.total_hours / maxHours) * 100);
                return (
                  <div key={a.id} onClick={() => navigate(`/assets/${a.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-input)'}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: `rgba(37,99,235,${0.25 - i * 0.04})`, color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        {a.tag && <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{a.tag}</span>}
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                      </div>
                      <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'var(--bg-card)', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #2563eb, #7c3aed)', borderRadius: 3 }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0, minWidth: 90 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#2563eb' }}>{a.total_hours}h</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.total_jobs} jobs · ${(a.total_cost ?? a.labour_cost ?? 0).toLocaleString()}</div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </motion.div>
      )}

      {showCreateModal && <WorkOrderCreateModal onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); navigate('/work-orders'); }} />}
    </div>
  );
}
